'use client'

import { Fragment, useEffect, useRef, useState } from 'react'

import { useCompareDockHeight } from '@/hooks/client/use-compare-dock-height'
import { MAX_COMPARE } from '@/hooks/client/use-compared'
import { useTranslation } from '@/hooks/client/use-translation'
import type { DirectoryRelease } from '@/lib/directory/aggregate-releases'
import { presentReleaseForFilters } from '@/lib/directory/filter-release-products'
import { pickCompareSlug } from '@/lib/directory/pick-compare-slug'
import { tw } from '@/lib/utils'
import { localizedHref } from '@/lib/utils/localized-href'

import { CompareDockItem } from './compare-dock-item'
import { trackDirectory } from './track-directory'
import type { DirectoryFilters } from './use-directory-reducer'
import { scrollFadeMask, useScrollFades } from './use-scroll-fades'
import { VsMark } from './vs-mark'

interface CompareDockProps {
  compared: ReadonlySet<string>
  releases: DirectoryRelease[]
  filters: DirectoryFilters
  onRemove: (slug: string) => void
}

// The tray: your picks joined by the pair page's "vs" medallion, and the one
// warm action on the page. One row everywhere — on phones the picks scroll
// sideways and the action carries the next step ("Pick one more"); wider
// screens add the hint beside it. Fixed to the bottom; slides up once.
export const CompareDock = ({ compared, releases, filters, onRemove }: CompareDockProps) => {
  const { t, locale } = useTranslation()

  // Gate on matched releases, not `compared.size`: stale stored slugs (a
  // release renamed/removed since it was picked) can keep the set non-empty
  // while nothing matches, which would render a "Compare 0" tray.
  const items = releases
    .filter((release) => compared.has(release.slug))
    .map((release) => presentReleaseForFilters(release, filters) ?? release)
  const count = items.length

  const [shown, setShown] = useState(false)
  useEffect(() => {
    setShown(count > 0)
  }, [count])

  const listRef = useRef<HTMLUListElement>(null)
  const fades = useScrollFades(listRef, [count])

  const ref = useCompareDockHeight<HTMLElement>(count)

  if (count === 0) {
    return null
  }

  const slugs = items.map((release) => pickCompareSlug(release, filters))
  const href = localizedHref(locale, `/hearing-aids/compare/${slugs.join('-vs-')}`)
  const canCompare = count >= 2
  const vs = t('compare.vs', 'vs.')
  const hint =
    count === 1
      ? t('directory.dock.pickOneMore', 'Pick one more to compare')
      : count >= MAX_COMPARE
        ? t('directory.dock.full', 'Four picked. Remove one to add another.')
        : t('directory.dock.upToFour', 'Add up to four')

  return (
    <section
      ref={ref}
      aria-label={t('directory.dock.label', 'Your comparison')}
      className={tw(
        'bg-navy fixed inset-x-0 bottom-0 z-50 text-white shadow-[0_-12px_32px_rgba(20,37,70,0.25)] transition-transform duration-300 ease-out motion-reduce:transition-none',
        shown ? 'translate-y-0' : 'translate-y-full'
      )}
    >
      <div className="mx-auto flex max-w-[1344px] items-center gap-3 px-4 pt-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] md:gap-6 md:px-12 md:py-4">
        {/* `relative` so the sr-only "vs" text inside stays clipped by this scroller.
            On phones an edge fades out only while chips are hidden past it, so a
            cut-off chip reads as "more to scroll" and a lone chip is never dimmed. */}
        <ul
          ref={listRef}
          style={{ maskImage: scrollFadeMask(fades), WebkitMaskImage: scrollFadeMask(fades) }}
          className="relative flex min-w-0 flex-1 items-center gap-2 overflow-x-auto py-0.5 [scrollbar-width:none] md:flex-wrap md:overflow-visible md:[mask-image:none]"
        >
          {items.map((item, i) => (
            <Fragment key={item.slug}>
              {i > 0 ? (
                <li className="shrink-0">
                  <VsMark label={vs} />
                </li>
              ) : null}
              <li className="shrink-0">
                <CompareDockItem item={item} onRemove={() => onRemove(item.slug)} />
              </li>
            </Fragment>
          ))}
        </ul>
        <p className="hidden shrink-0 text-[14px] text-white/70 md:block">{hint}</p>
        <a
          href={canCompare ? href : undefined}
          aria-disabled={!canCompare || undefined}
          data-tracking="ignore"
          title={!canCompare && count >= MAX_COMPARE ? hint : undefined}
          onClick={(e) => {
            if (!canCompare) {
              e.preventDefault()
              return
            }
            trackDirectory('directory-compare', { count })
          }}
          className={tw(
            'focus-visible:outline-sky inline-flex min-h-[44px] shrink-0 items-center justify-center rounded-xl px-4 text-[15px] font-semibold whitespace-nowrap no-underline transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 md:min-h-[48px] md:px-5',
            canCompare
              ? 'bg-merigold text-navy cursor-pointer hover:bg-white'
              : 'cursor-not-allowed bg-white/10 text-white/60'
          )}
        >
          {canCompare ? (
            <>
              <span className="md:hidden">{t('directory.dock.compareShort', 'Compare {{count}}', { count })}</span>
              <span className="hidden md:inline">
                {t('directory.dock.compare', 'Compare {{count}} hearing aids', { count })}
              </span>
            </>
          ) : (
            <>
              <span className="md:hidden">
                {count === 1
                  ? t('directory.dock.pickOneMoreShort', 'Pick one more')
                  : t('directory.row.compare', 'Compare')}
              </span>
              <span className="hidden md:inline">{t('directory.row.compare', 'Compare')}</span>
            </>
          )}
        </a>
      </div>
    </section>
  )
}
